import React, { useState, useCallback, useMemo, useEffect } from 'react';
import DataGrid from '../../common/DataGrid';
import { fetchStockValuation } from '../../../services/reportService';

const currencyFormatter = (params) => {
  if (params.value == null) return '';
  return Number(params.value).toFixed(2);
};

const qtyFormatter = (params) => {
  if (params.value == null) return '';
  return Number(params.value).toLocaleString();
};

const columns = [
  { field: 'item_id', headerName: 'Item ID', minWidth: 120, flex: 0.8 },
  { field: 'stock_no', headerName: 'Stock #', minWidth: 100, flex: 0.6 },
  { field: 'item_name', headerName: 'Description', flex: 2, minWidth: 200 },
  { field: 'category', headerName: 'Category', flex: 1, minWidth: 130 },
  { field: 'qty_on_hand', headerName: 'Qty on Hand', valueFormatter: qtyFormatter, type: 'rightAligned', minWidth: 110, flex: 0.7 },
  { field: 'cost_excl', headerName: 'Unit Cost', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 110, flex: 0.7 },
  { field: 'total_value', headerName: 'Cost Value', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 120, flex: 0.8 },
  { field: 'selling_price_excl', headerName: 'Sell Price', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 110, flex: 0.7 },
  { field: 'retail_value', headerName: 'Retail Value', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 120, flex: 0.8 },
];

export default function ValuationView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gridApi, setGridApi] = useState(null);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchStockValuation();
      setRows(data);
    } catch (err) {
      console.error('Error fetching stock valuation:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReport(); }, [loadReport]);

  const totals = useMemo(() => {
    if (!rows.length) return null;
    const totalQty = rows.reduce((s, r) => s + Number(r.qty_on_hand || 0), 0);
    const totalCostValue = rows.reduce((s, r) => s + Number(r.total_value || 0), 0);
    const totalRetailValue = rows.reduce((s, r) => s + Number(r.retail_value || 0), 0);
    const potentialProfit = totalRetailValue - totalCostValue;
    return { totalQty, totalCostValue, totalRetailValue, potentialProfit, itemCount: rows.length };
  }, [rows]);

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4 flex flex-wrap gap-3 items-end">
        <button
          onClick={loadReport}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md cursor-pointer hover:bg-blue-700 transition-colors"
        >
          Run Report
        </button>
        {gridApi && (
          <button
            onClick={() => gridApi.exportToCsv()}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
          >
            Export CSV
          </button>
        )}
      </div>

      {totals && (
        <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <TotalCard label="Items" value={totals.itemCount} isCurrency={false} isInteger />
          <TotalCard label="Total Qty" value={totals.totalQty} isCurrency={false} isInteger />
          <TotalCard label="Cost Value" value={totals.totalCostValue} />
          <TotalCard label="Retail Value" value={totals.totalRetailValue} />
          <TotalCard label="Potential Profit" value={totals.potentialProfit} />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center mt-10 text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="flex justify-center mt-10 text-gray-400 text-sm">
          Click "Run Report" to view current stock valuation.
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          <DataGrid
            rowData={rows}
            columns={columns}
            onGridReady={setGridApi}
            exportFileName="stock-valuation.csv"
            pagination
            paginationSize={20}
          />
        </div>
      )}
    </div>
  );
}

function TotalCard({ label, value, suffix, isCurrency = true, isInteger = false }) {
  let formatted;
  if (isInteger) {
    formatted = Number(value || 0).toLocaleString();
  } else if (isCurrency) {
    formatted = Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else {
    formatted = Number(value || 0).toFixed(2);
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm">
      <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-lg font-semibold text-gray-800 mt-0.5">
        {isCurrency && '$'}{formatted}{suffix || ''}
      </div>
    </div>
  );
}
